'use client';

import { useEffect } from 'react';
import Collapse from '@pixum/collapse';
import { ButtonVariants } from '@pixum/button';
import type { DeviceOrientedComponentProps } from '../../../types';
import type { FaqItemNode } from 'data-layer';
import { ButtonWithLinkMasking } from '../../buttonCollection/variants/ButtonWithLinkMasking';
import { RichTextContent, type RichTextDocument } from '../../richtText';

export interface FaqItemProps extends DeviceOrientedComponentProps {
  item: FaqItemNode;
  index: number;
  articleInternalName?: string;
  isExpanded: boolean;
  onToggle: (index: number) => void;
}

/**
 * FaqItem Component (Client Component)
 *
 * Renders a single FAQ item with a collapsible accordion.
 * Handles the collapse toggle and auto-scroll behavior on mobile devices.
 */
export function FaqItem(props: FaqItemProps) {
  const { item, index, articleInternalName, isExpanded, onToggle, device } = props;
  const isMobile = device === 'mobile';

  /**
   * Auto-scroll to expanded FAQ item on mobile devices
   */
  useEffect(() => {
    if (isMobile && isExpanded) {
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(`faq-${articleInternalName}-${index}`);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [isExpanded, isMobile, articleInternalName, index]);

  return (
    <Collapse
      id={`faq-${articleInternalName}-${index}`}
      expanded={isExpanded}
      headline={item.question}
      headlineLevel={3}
      onClick={() => onToggle(index)}
    >
      <div>
        {item.answer && <RichTextContent node={item.answer as RichTextDocument} />}
        {item.link && <ButtonWithLinkMasking link={item.link} device={device} variant={ButtonVariants.SECONDARY} />}
      </div>
    </Collapse>
  );
}
